import { Link } from "react-router-dom";
import { Users, Columns3, CheckSquare, CalendarDays, ArrowUpRight, UserCheck, Clock, UserX, PhoneCall, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/lib/api";
import { STATUSES, STATUS_LABELS, fullName, fmtDateTime } from "@/lib/crm";
import RequestsInbox from "@/components/RequestsInbox";

const STAGE_COLOR = { New: "#3B185F", Contacted: "#0284C7", Scheduled: "#D4AF37", Enrolled: "#059669", Closed: "#78716c" };

function Kpi({ icon: Icon, label, value, sub, to, color, testId }) {
  return (
    <Link to={to} className="group rounded-2xl border bg-white p-5 transition-[transform,border-color,box-shadow] hover:-translate-y-1 hover:border-[#D4AF37] hover:shadow-lg" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: `${color}14`, color }}><Icon className="h-5 w-5" /></span>
        <ArrowUpRight className="h-4 w-4 text-stone-300 group-hover:text-[#D4AF37]" />
      </div>
      <div className="mt-4 font-display text-3xl font-extrabold text-[#19082B]">{value}</div>
      <div className="text-sm font-semibold text-stone-700">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-stone-500">{sub}</div>}
    </Link>
  );
}

function AccessCard({ icon: Icon, label, value, tone, to, testId }) {
  const tones = { green: "bg-emerald-50 text-emerald-700 border-emerald-100 hover:border-emerald-300", amber: "bg-amber-50 text-amber-700 border-amber-100 hover:border-amber-300", red: "bg-rose-50 text-rose-700 border-rose-100 hover:border-rose-300" };
  return (
    <Link to={to} className={`group block rounded-2xl border p-5 transition-[transform,border-color,box-shadow] hover:-translate-y-1 hover:shadow-md ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between"><Icon className="h-5 w-5" /><ArrowUpRight className="h-4 w-4 opacity-30 group-hover:opacity-100" /></div>
      <div className="mt-4 font-display text-3xl font-extrabold">{value}</div>
      <div className="text-sm font-semibold">{label}</div>
    </Link>
  );
}

function AccessOverview() {
  const [stats] = useData("/admin/access-stats", null);
  if (!stats) return null;
  return (
    <section className="space-y-4 fade-up" data-testid="access-overview">
      <div className="flex items-end justify-between">
        <h2 className="font-display text-xl font-bold text-[#19082B]">Associate access</h2>
        <Link to="/admin/training?tab=users" className="text-sm font-semibold text-[#3B185F] hover:underline" data-testid="access-manage-link">Manage users</Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <AccessCard icon={UserCheck} label="Active" value={stats.active} tone="green" to="/admin/training?tab=users&filter=active" testId="access-active" />
        <AccessCard icon={Clock} label="Expiring soon (≤7 days)" value={stats.expiring_soon} tone="amber" to="/admin/training?tab=users&filter=expiring" testId="access-expiring" />
        <AccessCard icon={UserX} label="Expired" value={stats.expired} tone="red" to="/admin/training?tab=users&filter=expired" testId="access-expired" />
      </div>
    </section>
  );
}

function FunnelBar({ contacts }) {
  const total = contacts.length || 1;
  const counts = STATUSES.map((s) => ({ s, n: contacts.filter((c) => c.status === s).length }));
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid="pipeline-funnel">
      <div className="font-display text-base font-bold text-[#19082B] mb-3">Pipeline funnel</div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-stone-100">
        {counts.map(({ s, n }) => <div key={s} style={{ width: `${(n / total) * 100}%`, background: STAGE_COLOR[s] }} title={`${STATUS_LABELS[s]}: ${n}`} />)}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
        {counts.map(({ s, n }) => (
          <div key={s} className="text-center" data-testid={`funnel-${s}`}>
            <div className="font-display text-xl font-extrabold text-[#19082B]">{n}</div>
            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-500"><span className="h-2 w-2 rounded-full" style={{ background: STAGE_COLOR[s] }} />{STATUS_LABELS[s]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UpcomingTasks({ tasks }) {
  const open = tasks.filter((t) => !t.done).slice(0, 6);
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid="dashboard-upcoming-tasks">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-base font-bold text-[#19082B]">Upcoming follow-ups</div>
        <Link to="/tasks" className="text-xs font-semibold text-[#3B185F] hover:underline">View all</Link>
      </div>
      {!open.length && <p className="text-sm text-stone-500">No open tasks. You're all caught up.</p>}
      <ul className="space-y-2">
        {open.map((t) => (
          <li key={t.id} className="flex items-start gap-3 rounded-lg border p-2.5" data-testid={`dashboard-task-${t.id}`}>
            <CheckSquare className="h-4 w-4 text-[#D4AF37] mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#19082B] truncate">{t.title}</div>
              <div className="text-xs text-stone-500">{t.contact_name ? `${t.contact_name} · ` : ""}{t.type}{t.due_date ? ` · due ${t.due_date}${t.due_time ? " " + t.due_time : ""}` : ""}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecentActivity({ items }) {
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid="dashboard-recent-activity">
      <div className="font-display text-base font-bold text-[#19082B] mb-3">Recent activity</div>
      {!items.length && <p className="text-sm text-stone-500">No activity yet.</p>}
      <ol className="relative border-l border-[#D4AF37]/50 ml-1 space-y-3">
        {items.slice(0, 7).map((a) => (
          <li key={a.id} className="ml-4">
            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-[#3B185F]" />
            <div className="text-sm font-medium text-[#19082B]">{a.action}{a.contact_name && <span className="text-stone-500"> · {a.contact_name}</span>}</div>
            <div className="text-xs text-stone-400">{fmtDateTime(a.created_at)}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [contacts] = useData("/contacts", []);
  const [appts] = useData("/appointments", []);
  const [tasks] = useData("/reminders", []);
  const [activity] = useData("/activity", []);
  const today = new Date().toISOString().slice(0, 10);
  const openDeals = contacts.filter((c) => ["Contacted", "Scheduled", "Enrolled"].includes(c.status)).length;
  const sessions = contacts.filter((c) => c.status === "Scheduled").length;
  const openTasks = tasks.filter((t) => !t.done).length;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const newest = contacts[0];

  return (
    <div className="space-y-8">
      <section className="fade-up space-y-1">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C59B27] font-bold">{greet}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#19082B]" data-testid="dashboard-greeting">{user.name}</h1>
        <p className="text-sm text-stone-600">Here's your workspace at a glance — leads, deals, follow-ups and sessions.</p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Users} label="Total Leads" value={contacts.length} sub={`${contacts.filter((c) => c.status === "New").length} new inquiries`} to="/contacts" color="#3B185F" testId="stat-total-leads" />
        <Kpi icon={Columns3} label="Active Pipeline Deals" value={openDeals} sub="In progress" to="/pipeline" color="#D4AF37" testId="stat-open-deals" />
        <Kpi icon={CheckSquare} label="Upcoming Follow-ups" value={openTasks} sub="Open tasks" to="/tasks" color="#0284C7" testId="stat-pending-tasks" />
        <Kpi icon={CalendarDays} label="Sessions Scheduled" value={sessions} sub={`${appts.filter((a) => a.date >= today).length} appointments`} to="/calendar" color="#059669" testId="stat-sessions-scheduled" />
      </section>

      <FunnelBar contacts={contacts} />

      <section className="grid gap-6 lg:grid-cols-2">
        <UpcomingTasks tasks={tasks} />
        <RecentActivity items={activity} />
      </section>

      <section className="rounded-2xl bg-[#19082B] p-5 text-white flex flex-col sm:flex-row sm:items-center gap-4" data-testid="quick-tools">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold">Quick prospecting</div>
          <div className="font-display text-lg font-bold mt-0.5">{newest ? `Reach out to ${fullName(newest)}` : "Start prospecting"}</div>
          <p className="text-sm text-white/60">Dial or text your newest inquiry in one click.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/dialer" className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-semibold text-[#19082B] hover:bg-[#C59B27]" data-testid="quick-dial-btn"><PhoneCall className="h-4 w-4" />Dial</Link>
          <Link to="/sms" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20" data-testid="quick-sms-btn"><MessageSquare className="h-4 w-4" />SMS</Link>
        </div>
      </section>

      {user.role === "student" && <RequestsInbox />}
      {user.role === "admin" && (
        <>
          <AccessOverview />
          <section className="rounded-2xl border bg-white p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="admin-training-shortcut">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C59B27] font-bold">Admin</div>
              <div className="font-display text-xl font-bold mt-0.5 text-[#19082B]">Training Panel</div>
              <p className="text-sm text-stone-600">Assign tasks, review submissions, follow each associate's activity and manage access.</p>
            </div>
            <Link to="/admin/training" className="inline-flex items-center gap-1 rounded-full bg-[#3B185F] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#250E3F] transition-colors" data-testid="open-training-panel-btn">Open panel <ArrowUpRight className="h-4 w-4" /></Link>
          </section>
        </>
      )}
    </div>
  );
}
